import {
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { type SxProps, useTheme } from "@mui/system";
import useIsMobile from "hooks/useIsMobile";
import memoize from "memoize-one";
import {
  type CSSProperties,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { VariableSizeList as List } from "react-window";
import NoData from "../NoData";
import DataTableCard from "./Components/Table/Card";
import Column from "./Components/Table/Column";
import type { SearchProps } from "../Search";
import { Header } from "./Components/Table/Header";
import { Toolbar } from "./Components/Toolbar";
import styles from "./datatablev2.module.scss";
import { getColumns, hideColumns } from "./processor";
import CustomPagination from "pages/leads/CustomFiles/CustomPagination";

export const ITEM_HEIGHT = 64;
export const ITEM_PADDING_TOP = 8;

interface ToolBarOptions {
  showColumns: boolean;
  showFilters: boolean;
  showExport: boolean;
  showSearch: boolean;
}
export interface CustomColumn extends Record<string, any> {
  type?: "text" | "number" | "boolean" | "component";
  renderCell?: (params: Record<string, any>) => JSX.Element;
  valueFormatter?: (value: any) => string;
  sortComparator?: (a: any, b: any) => void;
  boolTitles?: string[];
  minWidth?: number;
  noFilter?: boolean;
  cellSx?: SxProps;
  link?: { href: (params: Record<string, any>) => string; target?: string };
}
export interface DataTablev2Props<T extends Record<string, any>> {
  onPaginationChange?: (page: number, perPage: number) => void;
  data: T[];
  filters?: Record<string, any>;
  setFilters?: Dispatch<SetStateAction<Record<string, any>>>;
  disableFilters?: boolean;
  filterColumnTableName : T[] | Record<string, any>;
  customColumns?: CustomColumn;
  toBeHidden?: string[];
  searchBarProps?: SearchProps;
  mainColor?: string;
  stripeBgColor?: string;
  pointerOnHover?: boolean;
  toolBar?: boolean;
  toolBarOptions?: Partial<ToolBarOptions>;
  onRowClicked?: (field: T, columnName?: string) => void;
  enablePagination?: boolean;
  sx?: SxProps;
  toolbarSx?: SxProps;
  toolSx?: SxProps;
  headerSx?: SxProps;
  paginationSx?: SxProps;
  onFilterChange?: (filters: Record<string, any>) => void;
  initialFilters?: Record<string, any>;
  hasCheckBox?: boolean;
  hideablesPreset?: string[];
  getColumnVisibility?: (columns: T) => void;
  headerTextColor?: string;
  rowsPerPage?: number;
  pagination: any;
  defaultColumnWidth?: number;
  tableSx?: CSSProperties;
  CustomToolBar?: () => JSX.Element;
  cellSx?: SxProps;
  collapsible?: string[];
  useKey?: string;
  appliedFilters?: Record<string, any>; 
  clearFilters: () => void; // Add clearFilters prop
  onFilterSubmit: (filters: string) => void; // Updated to match parent component
  applyCellStyling?: (column: string, item: T) => SxProps;
  noDataText?: string;
  customStatusOptions?: any; // Add custom status options prop
}

const DataTablev2 = <T extends Record<string, any>>(
  props: DataTablev2Props<T>
) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const {
    data = [],
    filterColumnTableName,
    pointerOnHover = true,
    customColumns = {},
    toBeHidden = [],
    searchBarProps,
    mainColor = theme.palette.primary.main,
    stripeBgColor = "rgba(204, 204, 204, 0.3)",
    toolBar = true,
    disableFilters,
    toolBarOptions = {
      showColumns: true,
      showFilters: true,
      showExport: true,
      showSearch: true,
    },
    onRowClicked = (field) => {},
    onFilterChange = () => {},
    initialFilters = {},
    hideablesPreset = [],
    getColumnVisibility = () => {},
    toolbarSx,
    toolSx,
    pagination,
    headerSx,
    sx,
    headerTextColor,
    rowsPerPage = 10,
    defaultColumnWidth = 100,
    tableSx,
    CustomToolBar = () => <></>,
    cellSx,
    collapsible = [],
    useKey = "id",
    clearFilters: parentClearFilters, // Rename to distinguish
    onFilterSubmit, // Receive the onFilterSubmit prop
    applyCellStyling = () => ({}),
    noDataText,
    customStatusOptions,
  } = props;

  const { filters: parentFilters, setFilters: setParentFilters } = props;
  const [filters, setFilters] = useState<Record<string, any>>(parentFilters || {});

  const [filteredData, setFilteredData] = useState<T[]>(data);
  const [allHideableColumns, setAllHideableColumns] = useState<string[]>([]);
  const [order, setOrder] = useState<Record<string, any>>({});
  const [page, setPage] = useState(pagination?.current_page ?? 1);
  const [perPage, setPerPage] = useState(pagination?.per_page ?? 10);

  // Sync internal page state with pagination prop from parent
  useEffect(() => {
    if (pagination?.current_page !== undefined && pagination.current_page !== page) {
      setPage(pagination.current_page);
    }
    if (pagination?.per_page !== undefined && pagination.per_page !== perPage) {
      setPerPage(pagination.per_page);
    }
  }, [pagination?.current_page, pagination?.per_page]);

  useEffect(() => {
    if (props.onPaginationChange) {
      props.onPaginationChange(
        pagination?.current_page ?? 1,
        pagination?.per_page ?? 10
      );
    }
  }, [page, perPage]);

  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [paginatedData, setPaginatedData] = useState<T[]>(
    [...data].splice(0, data?.length)
  );
  const [count, setCount] = useState(
    (filteredData?.length < 10 ? filteredData?.length : rowsPerPage) ?? 0
  );
  const columns = getColumns(data);
  const initialHideables = [...toBeHidden, ...hideColumns];
  const headers = columns.filter(
    (item) => ![...allHideableColumns, ...initialHideables].includes(item)
  );
  const handleRowClick = useCallback(
    (field: T, columnName: string) => onRowClicked(field, columnName),
    [onRowClicked]
  );

  const isMobile = useIsMobile();
  const HEIGHT = count * ITEM_HEIGHT;
  useEffect(() => {
    setCount(data?.length < 10 ? data?.length : rowsPerPage);
    setPaginatedData([...data].splice(0, rowsPerPage));
  }, [rowsPerPage, data]);

  // Update paginatedData when data, page, or perPage changes
  useEffect(() => {
    setCount(data?.length < 10 ? data?.length : perPage);
    setPaginatedData([...data].splice(0, perPage));
  }, [data, perPage, page]);

  // Reapply filters when data changes
  useEffect(() => {
    const applyFilters = (data: T[], filters: Record<string, any>) => {
      if (!Object.keys(filters).length) return data;
      return data.filter((item) => {
        return Object.entries(filters).every(([key, filter]) => {
          const value = item[key];
          if (!filter.keywords || !filter.keywords.length) return true;
          if (Array.isArray(filter.keywords)) {
            return filter.keywords.includes(value);
          }
          return value === filter.keywords;
        });
      });
    };

    setFilteredData(applyFilters(data, filters));
  }, [data, filters]);

  // Sync local filters with parent filters
  useEffect(() => {
    if (parentFilters) setFilters(parentFilters);
  }, [parentFilters]);

  const clearAllFilters = () => {
    setFilters({}); // Reset local filters
    if (props.onFilterSubmit) {
      props.onFilterSubmit(JSON.stringify({})); // Submit empty filters
    }
    setPage(1); // Reset pagination
    setPerPage(rowsPerPage);
    parentClearFilters(); // Call the parent's clearFilters function
  };

  const TableHeader = () => (
    <TableRow
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        boxSizing: "border-box",
        minWidth: "100%",
        width: "100%",
      }}
      component="div"
    >
      {headers.map((header) => (
        <Header
          key={header}
          defaultColumnWidth={defaultColumnWidth}
          customColumns={customColumns ?? {}}
          item={header}
          filteredData={filteredData}
          setFilteredData={setFilteredData}
          headers={columns}
          order={order}
          setOrder={setOrder}
          selectAll={selectAll}
          setSelectAll={setSelectAll}
          sx={headerSx}
          headerTextColor={headerTextColor}
        />
      ))}
    </TableRow>
  );
  const createItemData = memoize((classes, columns, data) => ({
    columns,
    classes,
    items: data,
  }));

  const itemData = createItemData({}, headers, paginatedData);

  const itemKey = (index: any, data: any) =>
    `${data.items[index][useKey]}-${index}`;

  return (
    <Grid
      container
      justifyContent="space-between"
      spacing={1}
      ref={ref}
      id="datatable-ctr"
      maxWidth="xl"
      width="100%"
    >
      {toolBar ? (
        <Grid item xs={12}>
          <Toolbar
            filterColumnTableName={filterColumnTableName}
            data={filteredData}
            initialData={data}
            headers={headers}
            disableFilters={disableFilters ?? false} 
            setFilteredData={setFilteredData}
            searchBarProps={searchBarProps}
            toolBarOptions={toolBarOptions}
            columnsToHide={initialHideables}
            customColumns={customColumns}
            allHideableColumns={allHideableColumns}
            setAllHideableColumns={setAllHideableColumns}
            onFilterChange={onFilterChange}
            initialFilters={initialFilters}
            hideablesPreset={hideablesPreset}
            getColumnVisibility={getColumnVisibility}
            sx={toolbarSx}
            toolSx={toolSx}
            onFilterSubmit={onFilterSubmit}
            clearFilters={clearAllFilters} // Pass the local clearAllFilters function
            filters={filters} // Pass filters to Toolbar
            setFilters={(newFilters: Record<string, any>) => {
              setFilters(newFilters); // Update local state
              setParentFilters?.(newFilters); // Update parent state if provided
            }} // Pass setFilters to Toolbar
            customStatusOptions={customStatusOptions} // Pass custom status options
          />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <CustomToolBar />
        </Grid>
      )}

      <Grid
        item
        xs={12}
        sx={sx}
        id="datatable-table-ctr"
        className={styles.tablegrid}
        justifyContent="flex-start"
      >
        {!isMobile ? (
          <TableContainer component={Paper} sx={{ width: "auto" }}>
            <Table
              sx={{
                tableLayout: "auto",
                ...tableSx,
                height: "100%",
                width: "auto",
                flexGrow: 1,
              }}
              aria-label="data-table"
              size="small"
              component="div"
            >
              <TableHead
                component="div"
                sx={{
                  boxShadow: `0px 1px 2px rgba(0, 0, 0, 0.3), 0px -1px 2px rgba(0, 0, 0, 0.3) inset
                `,
                }}
              >
                <TableHeader />
              </TableHead>
              <TableBody component="div" sx={{ width: "100%", height: "100%" }}>
                {Boolean(paginatedData.length) && (
                  <List
                    height={HEIGHT}
                    itemCount={paginatedData.length}
                    itemSize={() => ITEM_HEIGHT}
                    className={styles.datatablelist}
                    width="100%"
                    itemKey={itemKey}
                    itemData={{
                      ...itemData,
                      stripeBgColor,
                      pointerOnHover,
                      mainColor,
                      customColumns,
                      defaultColumnWidth,
                      handleRowClick,
                      cellSx,
                      applyCellStyling,
                    }}
                  >
                    {Column}
                  </List>
                )}
                {paginatedData?.length === 0 && (
                  <NoData
                    text={noDataText || "No data to display"}
                    sx={{
                      margin: "auto",
                      width: ref?.current?.clientWidth ?? "100%",
                    }}
                  />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Stack spacing={2}>
            {paginatedData.length &&
              paginatedData.map((item, i) => (
                <DataTableCard
                  key={item[useKey] ?? i}
                  item={item}
                  collapsible={collapsible}
                  handleRowClick={handleRowClick}
                  columns={columns.filter(
                    (item) =>
                      ![...allHideableColumns, ...initialHideables].includes(
                        item
                      )
                  )}
                  customColumns={customColumns}
                />
              ))}
            {paginatedData?.length === 0 && (
              <NoData
                text={noDataText || "No data to display"}
                sx={{
                  margin: "auto",
                  width: ref?.current?.clientWidth ?? "100%",
                }}
              />
            )}
          </Stack>
        )}
      </Grid>
      <CustomPagination
        totalCount={pagination?.total}
        perPage={perPage}
        currentPage={page}
        lastPage={pagination?.last_page}
        onPageChange={(newPage) => {
          setPage(newPage);
          props.onPaginationChange?.(newPage, perPage);
        }}
        onPerPageChange={(newPerPage) => {
          setPerPage(newPerPage);
          setPage(1);
          props.onPaginationChange?.(1, newPerPage);
        }}
      />
    </Grid>
  );
};

export default DataTablev2;
